import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, num, pct } from '../lib/format';
import { downloadXlsx, FMT_COP, FMT_INT, FMT_PCT, fileNameConFecha } from '../lib/xlsx';
import type {
  GastosPorInsumoResp,
  GastoPorInsumo,
  InversionAcumulada,
  ManoObraPeriodo,
  ManoObraResp,
  MesConYTD,
  PeriodoResumen,
  UtilidadProduccionMes,
} from '../api/types';
import { PageHeader } from '../components/PageHeader';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

type UnidadProy = 'dia' | 'semana' | 'mes';
type MesRow = MesConYTD & { idx: number };
type UtilProdRow = UtilidadProduccionMes;

function hoyISO() {
  return new Date().toISOString().slice(0, 10);
}
function haceNdiasISO(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function ReportesPage() {
  const { tiene } = useAuth();
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [unidad, setUnidad] = useState<UnidadProy>('semana');
  const [gastoDesde, setGastoDesde] = useState('');
  const [gastoHasta, setGastoHasta] = useState('');
  const [moDesde, setMoDesde] = useState(haceNdiasISO(30));
  const [moHasta, setMoHasta] = useState(hoyISO());
  const [moAgrupar, setMoAgrupar] = useState<UnidadProy>('dia');

  const mensualQ = useQuery({
    queryKey: ['reporte-mensual', anio],
    queryFn: () => API.get<{ anio: number; meses: MesConYTD[] }>(`/reportes/mensual?anio=${anio}`),
    enabled: tiene('reportes:read'),
  });

  const utilProdQ = useQuery({
    queryKey: ['utilidad-produccion', anio],
    queryFn: () =>
      API.get<{ anio: number; meses: UtilidadProduccionMes[] }>(
        `/reportes/utilidad-produccion?anio=${anio}`,
      ),
    enabled: tiene('reportes:read'),
  });

  const proyQ = useQuery({
    queryKey: ['reporte-proyeccion', unidad],
    queryFn: () => API.get<{ unidad: UnidadProy; periodos: PeriodoResumen[] }>(`/reportes/proyeccion?unidad=${unidad}`),
    enabled: tiene('reportes:read'),
  });

  const invQ = useQuery({
    queryKey: ['inversion-acumulada'],
    queryFn: () => API.get<InversionAcumulada>('/reportes/inversion-acumulada'),
    enabled: tiene('reportes:read'),
  });

  const gastosInsumoQ = useQuery({
    queryKey: ['gastos-por-insumo', gastoDesde, gastoHasta],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (gastoDesde) qs.set('desde', gastoDesde);
      if (gastoHasta) qs.set('hasta', gastoHasta);
      const suffix = qs.toString() ? `?${qs}` : '';
      return API.get<GastosPorInsumoResp>(`/reportes/gastos-por-insumo${suffix}`);
    },
    enabled: tiene('reportes:read'),
  });

  const manoObraQ = useQuery({
    queryKey: ['mano-de-obra', moDesde, moHasta, moAgrupar],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (moDesde) qs.set('desde', moDesde);
      if (moHasta) qs.set('hasta', moHasta);
      qs.set('agrupar', moAgrupar);
      return API.get<ManoObraResp>(`/reportes/mano-de-obra?${qs}`);
    },
    enabled: tiene('reportes:read'),
  });

  function exportarXlsx() {
    const meses = mensualQ.data?.meses ?? [];
    const periodos = proyQ.data?.periodos ?? [];
    const inv = invQ.data;

    const hojaInv = {
      name: 'Inversión acumulada',
      rows: [
        { concepto: 'Total activos (inversiones + equipos)', valor: inv?.totalInversiones ?? 0 },
        { concepto: 'Total aportes de socios', valor: inv?.totalAportes ?? 0 },
        { concepto: 'Depreciación mensual', valor: inv?.depreciacionMensual ?? 0 },
      ] as { concepto: string; valor: number }[],
      columns: [
        { header: 'Concepto', value: (r: { concepto: string; valor: number }) => r.concepto, width: 40 },
        { header: 'Valor', value: (r: { concepto: string; valor: number }) => r.valor, width: 16, numFmt: FMT_COP },
      ],
    };

    const MESES_EXPORT = MESES;

    const hojaMensual = {
      name: `Resumen ${anio}`,
      rows: meses.map((m, idx) => ({ ...m, nombreMes: MESES_EXPORT[idx] })),
      columns: [
        { header: 'Mes', value: (m: MesConYTD & { nombreMes: string }) => m.nombreMes, width: 12 },
        { header: 'Ingresos', value: (m: MesConYTD) => m.ingresos, width: 14, numFmt: FMT_COP },
        { header: 'Gastos', value: (m: MesConYTD) => m.gastos, width: 14, numFmt: FMT_COP },
        { header: 'Compras inventario', value: (m: MesConYTD) => m.compras, width: 18, numFmt: FMT_COP },
        { header: 'Aportes', value: (m: MesConYTD) => m.aportes, width: 14, numFmt: FMT_COP },
        { header: 'Utilidad', value: (m: MesConYTD) => m.utilidad, width: 14, numFmt: FMT_COP },
        { header: 'Ingresos YTD', value: (m: MesConYTD) => m.ingresosYTD, width: 14, numFmt: FMT_COP },
        { header: 'Gastos YTD', value: (m: MesConYTD) => m.gastosYTD, width: 14, numFmt: FMT_COP },
        { header: 'Utilidad YTD', value: (m: MesConYTD) => m.utilidadYTD, width: 14, numFmt: FMT_COP },
        { header: 'Margen', value: (m: MesConYTD) => m.margenPct, width: 10, numFmt: FMT_PCT },
        { header: 'Paq. producidos', value: (m: MesConYTD) => m.paquetesProducidos, width: 14, numFmt: FMT_INT },
        { header: 'Ingreso producción', value: (m: MesConYTD) => m.ingresoProduccion, width: 16, numFmt: FMT_COP },
      ],
    };

    const labelUnidad = unidad === 'dia' ? 'Día' : unidad === 'semana' ? 'Semana' : 'Mes';
    const hojaProy = {
      name: `Proyección por ${labelUnidad.toLowerCase()}`,
      rows: periodos,
      columns: [
        { header: 'Período', value: (p: PeriodoResumen) => p.periodo.slice(0, 10), width: 14 },
        { header: 'Ingresos', value: (p: PeriodoResumen) => p.ingresos, width: 14, numFmt: FMT_COP },
        { header: 'Gastos', value: (p: PeriodoResumen) => p.gastos, width: 14, numFmt: FMT_COP },
        { header: 'Compras inventario', value: (p: PeriodoResumen) => p.compras, width: 18, numFmt: FMT_COP },
        { header: 'Aportes', value: (p: PeriodoResumen) => p.aportes, width: 14, numFmt: FMT_COP },
        { header: 'Utilidad', value: (p: PeriodoResumen) => p.utilidad, width: 14, numFmt: FMT_COP },
        { header: 'Paquetes', value: (p: PeriodoResumen) => p.paquetesProducidos, width: 12, numFmt: FMT_INT },
        { header: 'Ingreso producción', value: (p: PeriodoResumen) => p.ingresoProduccion, width: 16, numFmt: FMT_COP },
      ],
    };

    const hojaGastos = {
      name: 'Gastos por insumo',
      rows: gastosInsumoQ.data?.items ?? [],
      columns: [
        { header: 'Insumo', value: (g: GastoPorInsumo) => g.insumoNombre, width: 24 },
        { header: 'Unidad', value: (g: GastoPorInsumo) => g.unidad ?? '', width: 10 },
        { header: 'Cantidad total', value: (g: GastoPorInsumo) => g.totalCantidad, width: 14, numFmt: FMT_INT },
        { header: 'Monto total', value: (g: GastoPorInsumo) => g.totalMonto, width: 16, numFmt: FMT_COP },
        { header: '# movimientos', value: (g: GastoPorInsumo) => g.conteo, width: 14, numFmt: FMT_INT },
      ],
    };

    const hojaMO = {
      name: 'Mano de obra',
      rows: manoObraQ.data?.periodos ?? [],
      columns: [
        { header: 'Período', value: (p: ManoObraPeriodo) => p.periodo, width: 14 },
        { header: 'Horas', value: (p: ManoObraPeriodo) => p.totalHoras, width: 10, numFmt: FMT_INT },
        { header: 'Monto', value: (p: ManoObraPeriodo) => p.totalMonto, width: 16, numFmt: FMT_COP },
        { header: '# registros', value: (p: ManoObraPeriodo) => p.conteo, width: 12, numFmt: FMT_INT },
      ],
    };

    downloadXlsx(fileNameConFecha(`reporte-${anio}`), [
      hojaInv,
      hojaMensual,
      hojaProy,
      hojaGastos,
      hojaMO,
    ]);
  }

  const hayAlgunDato =
    (mensualQ.data?.meses?.length ?? 0) > 0 ||
    (proyQ.data?.periodos?.length ?? 0) > 0 ||
    Boolean(invQ.data);

  const mensualRows: MesRow[] = (mensualQ.data?.meses ?? []).map((m, idx) => ({ ...m, idx }));

  const mensualCols: Column<MesRow>[] = [
    { key: 'mes', header: 'Mes', mobile: 'primary', render: (m) => <span className="font-medium">{MESES[m.idx]}</span> },
    {
      key: 'util',
      header: 'Utilidad',
      mobile: 'secondary',
      align: 'right',
      render: (m) => (
        <span className={m.utilidad >= 0 ? 'font-medium text-emerald-700' : 'font-medium text-red-600'}>
          {cop(m.utilidad)}
        </span>
      ),
    },
    { key: 'ing', header: 'Ingresos', align: 'right', render: (m) => <span className="text-emerald-700">{cop(m.ingresos)}</span> },
    { key: 'gas', header: 'Gastos', align: 'right', render: (m) => <span className="text-red-600">{cop(m.gastos)}</span> },
    { key: 'comp', header: 'Compras inv.', align: 'right', render: (m) => <span className="text-amber-700">{cop(m.compras)}</span> },
    { key: 'ap', header: 'Aportes', align: 'right', render: (m) => <span className="text-blue-700">{cop(m.aportes)}</span> },
    { key: 'ingYTD', header: 'Ingresos YTD', align: 'right', render: (m) => cop(m.ingresosYTD) },
    {
      key: 'utYTD', header: 'Utilidad YTD', align: 'right',
      render: (m) => <span className={m.utilidadYTD >= 0 ? 'font-medium' : 'font-medium text-red-600'}>{cop(m.utilidadYTD)}</span>,
    },
    { key: 'margen', header: 'Margen', align: 'right', render: (m) => pct(m.margenPct) },
    { key: 'paq', header: 'Paq. prod.', align: 'right', render: (m) => num(m.paquetesProducidos, 0) },
  ];

  const utilProdCols: Column<UtilProdRow>[] = [
    {
      key: 'mes',
      header: 'Mes',
      mobile: 'primary',
      render: (u) => <span className="font-medium">{MESES[u.mes]}</span>,
    },
    {
      key: 'ganancia',
      header: 'Ganancia',
      mobile: 'secondary',
      align: 'right',
      render: (u) => (
        <span
          className={
            u.gananciaProduccion >= 0
              ? 'font-medium text-emerald-700'
              : 'font-medium text-red-600'
          }
        >
          {cop(u.gananciaProduccion)}
        </span>
      ),
    },
    {
      key: 'ing',
      header: 'Ingreso prod.',
      align: 'right',
      render: (u) => <span className="text-emerald-700">{cop(u.ingresoProduccion)}</span>,
    },
    {
      key: 'costo',
      header: 'Costo prod.',
      align: 'right',
      render: (u) => <span className="text-red-600">{cop(u.costoProduccion)}</span>,
    },
    { key: 'paq', header: 'Paquetes', align: 'right', render: (u) => num(u.paquetes, 0) },
    { key: 'margen', header: 'Margen', align: 'right', render: (u) => pct(u.margenPct) },
  ];

  const utilProdRows: UtilProdRow[] = utilProdQ.data?.meses ?? [];

  const gastoInsumoCols: Column<GastoPorInsumo>[] = [
    {
      key: 'ins',
      header: 'Insumo',
      mobile: 'primary',
      render: (g) => <span className="font-medium">{g.insumoNombre}</span>,
    },
    {
      key: 'monto',
      header: 'Monto',
      mobile: 'secondary',
      align: 'right',
      render: (g) => <span className="font-medium text-red-600">{cop(g.totalMonto)}</span>,
    },
    {
      key: 'cant',
      header: 'Cantidad',
      align: 'right',
      render: (g) =>
        g.totalCantidad > 0 ? `${num(g.totalCantidad, 2)} ${g.unidad ?? ''}`.trim() : '—',
    },
    { key: 'n', header: '# mov.', align: 'right', render: (g) => num(g.conteo, 0) },
  ];

  const moCols: Column<ManoObraPeriodo>[] = [
    {
      key: 'per',
      header: 'Período',
      mobile: 'primary',
      render: (p) => <span className="font-medium">{p.periodo}</span>,
    },
    {
      key: 'monto',
      header: 'Monto',
      mobile: 'secondary',
      align: 'right',
      render: (p) => <span className="font-medium text-red-600">{cop(p.totalMonto)}</span>,
    },
    {
      key: 'horas',
      header: 'Horas',
      align: 'right',
      render: (p) => (p.totalHoras > 0 ? num(p.totalHoras, 1) : '—'),
    },
    { key: 'n', header: '# reg.', align: 'right', render: (p) => num(p.conteo, 0) },
  ];

  const proyCols: Column<PeriodoResumen>[] = [
    { key: 'per', header: 'Período', mobile: 'primary', render: (p) => <span className="font-medium">{p.periodo.slice(0, 10)}</span> },
    {
      key: 'util',
      header: 'Utilidad',
      mobile: 'secondary',
      align: 'right',
      render: (p) => (
        <span className={p.utilidad >= 0 ? 'font-medium text-emerald-700' : 'font-medium text-red-600'}>
          {cop(p.utilidad)}
        </span>
      ),
    },
    { key: 'ing', header: 'Ingresos', align: 'right', render: (p) => <span className="text-emerald-700">{cop(p.ingresos)}</span> },
    { key: 'gas', header: 'Gastos', align: 'right', render: (p) => <span className="text-red-600">{cop(p.gastos)}</span> },
    { key: 'comp', header: 'Compras inv.', align: 'right', render: (p) => <span className="text-amber-700">{cop(p.compras)}</span> },
    { key: 'paq', header: 'Paquetes', align: 'right', render: (p) => num(p.paquetesProducidos, 0) },
    { key: 'ingProd', header: 'Ingreso producción', align: 'right', render: (p) => cop(p.ingresoProduccion) },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Reportes y proyecciones"
        subtitle="Resumen mensual YTD, proyección por período e inversión acumulada."
        action={
          <button
            type="button"
            className="btn-secondary"
            onClick={exportarXlsx}
            disabled={!hayAlgunDato}
          >
            ⬇ Descargar .xlsx
          </button>
        }
      />

      <div className="md:hidden">
        <button
          type="button"
          className="btn-secondary w-full"
          onClick={exportarXlsx}
          disabled={!hayAlgunDato}
        >
          ⬇ Descargar reporte .xlsx
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <MiniCard title="Total invertido" value={cop((invQ.data?.totalInversiones ?? 0) + (invQ.data?.totalAportes ?? 0))} hint="Activos + aportes" />
        <MiniCard title="Activos" value={cop(invQ.data?.totalInversiones)} hint="Equipos + inv." />
        <MiniCard title="Aportes socios" value={cop(invQ.data?.totalAportes)} />
        <MiniCard title="Depre./mes" value={cop(invQ.data?.depreciacionMensual)} hint="Base costo fijo" />
      </div>

      <div className="card">
        <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h2 className="font-semibold">Resumen mensual {anio} <span className="text-slate-500 font-normal text-sm">(con YTD)</span></h2>
          <div className="flex gap-2 items-center">
            <button type="button" className="btn-secondary tap" onClick={() => setAnio((a) => a - 1)} aria-label="Año anterior">◄</button>
            <input type="number" inputMode="numeric" className="input w-20 text-center" value={anio} onChange={(e) => setAnio(Number(e.target.value) || anio)} />
            <button type="button" className="btn-secondary tap" onClick={() => setAnio((a) => a + 1)} aria-label="Año siguiente">►</button>
          </div>
        </div>
        <ResponsiveTable<MesRow>
          columns={mensualCols}
          rows={mensualRows}
          rowKey={(m) => m.idx}
          loading={mensualQ.isLoading}
          empty="Sin datos."
        />
      </div>

      <div className="card">
        <div className="p-3 sm:p-4 border-b border-slate-200">
          <h2 className="font-semibold">
            Utilidad de producción {anio}{' '}
            <span className="text-slate-500 font-normal text-sm">
              (ingreso esperado − costo imputado a cada lote)
            </span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Basado en los lotes registrados: usa el precio de venta del lote y los insumos
            consumidos según snapshot. No depende de cuándo cobraste o pagaste.
          </p>
        </div>
        <ResponsiveTable<UtilProdRow>
          columns={utilProdCols}
          rows={utilProdRows}
          rowKey={(u) => u.mes}
          loading={utilProdQ.isLoading}
          empty="Sin lotes registrados en este año."
        />
      </div>

      <div className="card">
        <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <h2 className="font-semibold">Proyección por período</h2>
          <div className="flex gap-2 overflow-x-auto">
            {(['dia', 'semana', 'mes'] as UnidadProy[]).map((u) => (
              <button
                key={u}
                type="button"
                className={`chip whitespace-nowrap ${unidad === u ? 'chip-active' : ''}`}
                onClick={() => setUnidad(u)}
              >
                {u === 'dia' ? 'Día' : u === 'semana' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveTable<PeriodoResumen>
          columns={proyCols}
          rows={proyQ.data?.periodos}
          rowKey={(p) => p.periodo}
          loading={proyQ.isLoading}
          empty="Sin datos."
        />
      </div>

      <div className="card">
        <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <h2 className="font-semibold">
              Gastos por insumo
              <span className="text-slate-500 font-normal text-sm">
                {' '}(maíz, gas, bolsas, agua, mano de obra…)
              </span>
            </h2>
            <div className="text-sm">
              <span className="text-slate-500">Total: </span>
              <span className="font-semibold text-red-600">
                {cop(gastosInsumoQ.data?.total ?? 0)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="label">Desde</label>
              <input
                type="date"
                className="input"
                value={gastoDesde}
                onChange={(e) => setGastoDesde(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Hasta</label>
              <input
                type="date"
                className="input"
                value={gastoHasta}
                onChange={(e) => setGastoHasta(e.target.value)}
              />
            </div>
            {(gastoDesde || gastoHasta) && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setGastoDesde('');
                  setGastoHasta('');
                }}
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
        <ResponsiveTable<GastoPorInsumo>
          columns={gastoInsumoCols}
          rows={gastosInsumoQ.data?.items}
          rowKey={(g) => g.insumoId ?? g.insumoNombre}
          loading={gastosInsumoQ.isLoading}
          empty="Sin gastos registrados en el rango."
        />
      </div>

      <div className="card">
        <div className="p-3 sm:p-4 border-b border-slate-200 flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <h2 className="font-semibold">Mano de obra</h2>
            <div className="text-sm flex gap-3">
              <div>
                <span className="text-slate-500">Total horas: </span>
                <span className="font-semibold">
                  {num(manoObraQ.data?.totalHoras ?? 0, 1)}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Total gastado: </span>
                <span className="font-semibold text-red-600">
                  {cop(manoObraQ.data?.total ?? 0)}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="label">Desde</label>
              <input
                type="date"
                className="input"
                value={moDesde}
                onChange={(e) => setMoDesde(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Hasta</label>
              <input
                type="date"
                className="input"
                value={moHasta}
                onChange={(e) => setMoHasta(e.target.value)}
              />
            </div>
            <div className="flex gap-2 ml-auto">
              {(['dia', 'semana', 'mes'] as UnidadProy[]).map((u) => (
                <button
                  key={u}
                  type="button"
                  className={`chip whitespace-nowrap ${moAgrupar === u ? 'chip-active' : ''}`}
                  onClick={() => setMoAgrupar(u)}
                >
                  {u === 'dia' ? 'Por día' : u === 'semana' ? 'Por semana' : 'Por mes'}
                </button>
              ))}
            </div>
          </div>
        </div>
        <ResponsiveTable<ManoObraPeriodo>
          columns={moCols}
          rows={manoObraQ.data?.periodos}
          rowKey={(p) => p.periodo}
          loading={manoObraQ.isLoading}
          empty='Sin registros de mano de obra en el rango. Se calcula a partir de los lotes de producción (personas × horas × precio de mano de obra).'
        />
      </div>
    </div>
  );
}

function MiniCard({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <div className="card p-3 sm:p-4">
      <div className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="text-base sm:text-2xl font-semibold mt-0.5 sm:mt-1 break-words">{value}</div>
      {hint && <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 sm:mt-1">{hint}</div>}
    </div>
  );
}
